/**
 * Portal subdomain configuration
 * Handles detection and routing between main site and dealer portal
 */

// Production subdomain configuration
export const PORTAL_HOSTNAME = 'portal.autonewsweekly.com';
export const MAIN_HOSTNAME = 'www.autonewsweekly.com';

// Alternative hostnames (for staging/preview environments)
const PORTAL_HOSTNAMES = [
  'portal.autonewsweekly.com',
  'portal-autonewsweekly.lovable.app', // Potential staging subdomain
];

const MAIN_HOSTNAMES = [
  'www.autonewsweekly.com',
  'autonewsweekly.com',
  'gear-gossip-news.lovable.app', // Published URL
];

/**
 * Check if currently on the dealer portal subdomain
 */
export function isPortalSubdomain(): boolean {
  const hostname = window.location.hostname;
  
  // In development, treat as portal if on /dealer/* routes
  if (hostname === 'localhost' || hostname.includes('preview--')) {
    return window.location.pathname.startsWith('/dealer');
  }
  
  return PORTAL_HOSTNAMES.some(h => hostname === h || hostname.endsWith(`.${h}`));
}

/**
 * Check if currently on the main public site
 */
export function isMainSite(): boolean {
  const hostname = window.location.hostname;
  
  // In development, treat as main site if NOT on /dealer/* routes
  if (hostname === 'localhost' || hostname.includes('preview--')) {
    return !window.location.pathname.startsWith('/dealer');
  }
  
  return MAIN_HOSTNAMES.some(h => hostname === h || hostname.endsWith(`.${h}`));
}

/**
 * Get the full URL for the portal subdomain
 */
export function getPortalUrl(path: string = '/'): string {
  const hostname = window.location.hostname;
  
  // In development/preview/staging, just use the path (relative navigation)
  if (
    hostname === 'localhost' || 
    hostname.includes('preview--') ||
    hostname.includes('.lovableproject.com') ||
    hostname.includes('.lovable.app')
  ) {
    return path;
  }
  
  // On production custom domains, return the full portal subdomain URL
  return `https://${PORTAL_HOSTNAME}${path}`;
}

/**
 * Get the full URL for the main site
 */
export function getMainSiteUrl(path: string = '/'): string {
  // In development/preview, just use the path
  if (window.location.hostname === 'localhost' || window.location.hostname.includes('preview--')) {
    return path;
  }
  
  return `https://${MAIN_HOSTNAME}${path}`;
}

/**
 * Redirect from main site dealer routes to portal subdomain
 * Returns true if redirect was triggered
 */
export function redirectToPortalIfNeeded(): boolean {
  const hostname = window.location.hostname;
  const pathname = window.location.pathname;
  
  // Skip in development/preview environments
  if (hostname === 'localhost' || hostname.includes('preview--')) {
    return false;
  }
  
  // If on main site and hitting /dealer/* routes, redirect to portal
  if (isMainSite() && pathname.startsWith('/dealer')) {
    const portalUrl = getPortalUrl(pathname + window.location.search);
    window.location.replace(portalUrl);
    return true;
  }
  
  return false;
}

/**
 * Redirect from portal to main site for non-dealer routes
 * Returns true if redirect was triggered
 */
export function redirectToMainSiteIfNeeded(): boolean {
  const hostname = window.location.hostname;
  const pathname = window.location.pathname;
  
  // Skip in development/preview environments
  if (hostname === 'localhost' || hostname.includes('preview--')) {
    return false;
  }
  
  // Allowed routes on portal subdomain
  const allowedPortalPaths = [
    '/dealer/login',
    '/dealer/dashboard',
    '/dealer/leads',
    '/dealer/settings',
    '/dealer/sales',
    '/dealer/account',
    '/dealer/accept-invite',
  ];
  
  // If on portal and NOT on an allowed dealer route, redirect to main site
  if (isPortalSubdomain()) {
    const isAllowedPath = allowedPortalPaths.some(p => 
      pathname === p || pathname.startsWith(p + '/')
    );
    
    if (!isAllowedPath) {
      const mainUrl = getMainSiteUrl(pathname + window.location.search);
      window.location.replace(mainUrl);
      return true;
    }
  }
  
  return false;
}

/**
 * Get the appropriate login URL based on current subdomain
 */
export function getDealerLoginUrl(returnPath?: string): string {
  const baseUrl = '/dealer/login';
  if (returnPath) {
    return `${baseUrl}?next=${encodeURIComponent(returnPath)}`;
  }
  return baseUrl;
}

/**
 * Get the post-login redirect URL based on user role
 */
export function getPostLoginRedirect(role: 'dealer_admin' | 'salesperson' | 'admin'): string {
  switch (role) {
    case 'dealer_admin':
      return '/dealer/dashboard';
    case 'salesperson':
      return '/dealer/sales';
    case 'admin':
      return '/admin';
    default:
      return '/dealer/dashboard';
  }
}
